import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Snackbar,
  Chip,
  useMediaQuery,
  useTheme as useMuiTheme,
  styled,
  Link as MuiLink,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from "@mui/material";
import {
  Settings,
  ToggleOn,
  ToggleOff,
  Save,
  Refresh,
  Lock,
  LockOpen,
  School,
  DateRange,
  Warning,
  CheckCircle,
  Dashboard,
  People,
  AdminPanelSettings,
  ArrowBack,
  Logout,
  Close as CloseIcon,
  Info,
  Publish,
  Group,
  HowToReg,
  Assessment,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import axios from "axios";
import { baseUrl, logOutUser, getUser } from "../../common/utils";
import { useHistory, useLocation } from "react-router-dom";
import SchoolActivityStats from "./SchoolActivityStats";

// ============================================
// Modern Dashboard Theme (MUI5)
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    success: {
      main: "#059669",
    },
    error: {
      main: "#dc2626",
    },
    info: {
      main: "#3b82f6",
    },
    warning: {
      main: "#f59e0b",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: "6px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            borderColor: "#243b5a",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "6px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f8f9fa",
          fontSize: "13px",
          color: "#4b5563",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: "#243b5a",
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

const NavMenu = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "12px",
  flexWrap: "wrap",
});

const NavButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  color: active ? "#ffffff" : "#e0e7ff",
  backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "20px",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#ffffff",
  },
  "& svg": {
    marginRight: "8px",
    fontSize: "18px",
  },
}));

const StatusCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
}));

// ============================================
// AdminSettings Component
// ============================================
const AdminSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sessionYear, setSessionYear] = useState("");
  const [registrationStatus, setRegistrationStatus] = useState<1 | 2 | 3>(1); // 1 = normal, 2 = late, 3 = pos_late
  const [systemStatus, setSystemStatus] = useState<0 | 1>(1); // 0 = closed, 1 = open
  const [currentSettings, setCurrentSettings] = useState<any>(null);
  
  // Snackbar State
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const history = useHistory();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const user = getUser();
  const userData = {
    name: user?.name || "ADMIN USER",
    lgaCode: user?.lgaCode || "",
    schoolCode: user?.schoolCode || "",
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleConfirmAction = () => {
    confirmDialog.onConfirm();
    handleCloseConfirmDialog();
  };

  // Fetch current settings
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/getSettings`, {});
      
      if (res.data.status && res.data.data) {
        const settings = res.data.data;
        setCurrentSettings(settings);
        setSessionYear(settings.sessionYr || "");
        
        const statusValue = settings.status;
        const isOpen = statusValue === 1 || statusValue === "1";
        setSystemStatus(isOpen ? 1 : 0);
        
        const rawRegStatus = settings.reg_status ?? (isOpen ? 1 : 2);
        const regStatus = parseInt(rawRegStatus as string, 10);
        setRegistrationStatus(regStatus === 2 ? 2 : regStatus === 3 ? 3 : 1);
      } else {
        // No settings found, set defaults
        setSessionYear("2025/2026");
        setRegistrationStatus(1); 
        setSystemStatus(1);
      }
    } catch (e) { 
      console.error("Error fetching settings:", e);
      // Set defaults on error
      setSessionYear("2025/2026");
      setRegistrationStatus(1);
    } finally { 
      setLoading(false); 
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    if (!sessionYear) {
      showSnackbar("Please enter a session year", 'warning');
      return;
    }

    // Validate session year format (YYYY/YYYY)
    const sessionRegex = /^\d{4}\/\d{4}$/;
    if (!sessionRegex.test(sessionYear)) {
      showSnackbar("Invalid session year format. Use YYYY/YYYY (e.g., 2025/2026)", 'warning');
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/setSettings`, {
        sessionYr: sessionYear,
        status: systemStatus === 1 ? "1" : "2",
        reg_status: registrationStatus
      });
      
      if (res.data.status) {
        showSnackbar(res.data.message || "Settings saved successfully", 'success');
        await fetchSettings(); // Refresh settings
      } else {
        showSnackbar(res.data.message || "Failed to save settings", 'error');
      }
    } catch (e) { 
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally { 
      setSaving(false); 
    }
  };

  // Handle registration status change
  const handleRegistrationStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRegistrationStatus(parseInt(event.target.value) as 1 | 2 | 3);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleMenuClick = (menu: string) => {
    history.push("/" + menu);
  };

  const handleLogout = () => {
    logOutUser();
    history.push("/login");
  };

  // Closed by request of the boss - rendering nothing
  // return null;

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER */}
            <Header>
              <HeaderTopRow>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Admin Settings
                    </Typography>
                  </Box>
                </Box>

                <SchoolInfoBox>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                  >
                    {userData.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    LGA: {userData.lgaCode} | Code: {userData.schoolCode}
                  </Typography>
                </SchoolInfoBox>
              </HeaderTopRow>

              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>

              <NavMenu>
                <NavButton
                  active={location.pathname === "/admin_dashboard"}
                  onClick={() => handleMenuClick("admin_dashboard")}
                  startIcon={<Dashboard />}
                >
                  Admin Dashboard
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_students"}
                  onClick={() => handleMenuClick("admin_students")}
                  startIcon={<People />}
                >
                  Students
                </NavButton>
                <NavButton
                  active={location.pathname === "/lga_student_database"}
                  onClick={() => handleMenuClick("lga_student_database")}
                  startIcon={<Group />}
                >
                  LGA Student Database
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_schools"}
                  onClick={() => handleMenuClick("admin_schools")}
                  startIcon={<School />}
                >
                  View Schools
                </NavButton>
                <NavButton
                  active={location.pathname === "/add_result"}
                  onClick={() => handleMenuClick("add_result")}
                  startIcon={<HowToReg />}
                >
                  Add Result
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_settings"}
                  onClick={() => handleMenuClick("admin_settings")}
                  startIcon={<Settings />}
                >
                  Settings
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_management"}
                  onClick={() => handleMenuClick("admin_management")}
                  startIcon={<AdminPanelSettings />}
                >
                  Management Console
                </NavButton>

                <NavButton
                  active={location.pathname === "/admin_stats"}
                  onClick={() => handleMenuClick("admin_stats")}
                  startIcon={<Assessment />}
                >
                  Activity Stats
                </NavButton>
              </NavMenu>
            </Header>


            {/* MAIN CONTENT */}
            <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
              <Button
                onClick={() => history.goBack()}
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#243b5a",
                  fontSize: "13px",
                  textTransform: "none",
                  p: 0,
                  mb: 3,
                  '&:hover': {
                    textDecoration: "underline",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Back
              </Button>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={800} color="primary">
                  Settings
                </Typography>
                <Typography color="text.secondary">
                  Manage system settings and preferences
                </Typography>
              </Box>

              {loading && !currentSettings ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {/* System Settings Card */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 4, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Settings sx={{ color: '#243b5a', fontSize: 28 }} />
                        <Typography variant="h5" fontWeight="bold">
                          Manage System Settings
                        </Typography>
                      </Box>

                      <Grid container spacing={3}>
                        {/* Left Column - Input Fields */}
                        <Grid item xs={12} md={6}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                              1. Edit Session Year
                            </Typography>
                            <TextField
                              fullWidth
                              label="Session Year"
                              value={sessionYear}
                              onChange={(e) => setSessionYear(e.target.value)}
                              placeholder="2025/2026"
                              helperText="Format: YYYY/YYYY (e.g., 2025/2026)"
                              InputProps={{
                                startAdornment: <DateRange sx={{ mr: 1, color: '#9ca3af' }} />,
                              }}
                              sx={{ mb: 3 }}
                            />

                             <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2, mt: 3 }}>
                                2. System Operation Mode
                              </Typography>
                              <Paper sx={{ p: 2, border: `2px solid #243b5a`, mb: 3, bgcolor: systemStatus === 1 ? '#f0fdf4' : '#fef2f2' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {systemStatus === 1 ? <LockOpen sx={{ color: '#059669' }} /> : <Lock sx={{ color: '#dc2626' }} />}
                                    <Box>
                                      <Typography variant="body1" fontWeight="bold" color={systemStatus === 1 ? "#059669" : "#dc2626"}>
                                        System is {systemStatus === 1 ? 'OPEN' : 'CLOSED'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Toggle to open or close the entire portal for users
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Switch 
                                    checked={systemStatus === 1}
                                    onChange={(e) => setSystemStatus(e.target.checked ? 1 : 0)}
                                    color="success"
                                  />
                                </Box>
                              </Paper>

                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2, mt: 3 }}>
                                3. Set Registration Status
                              </Typography>
                              <Paper sx={{ p: 3, border: `2px solid #243b5a` }}>
                                <FormControl component="fieldset">
                                  <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold', color: '#243b5a' }}>
                                    Select Registration Period
                                  </FormLabel>
                                  <RadioGroup
                                    value={registrationStatus}
                                    onChange={handleRegistrationStatusChange}
                                    sx={{ gap: 1 }}
                                  >
                                    <FormControlLabel
                                      value={1}
                                      control={<Radio color="primary" />}
                                      label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <CheckCircle sx={{ color: '#059669' }} />
                                          <Box>
                                            <Typography variant="body1" fontWeight="bold" color="#059669">
                                              Normal Registration
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Standard registration period - all students register normally
                                            </Typography>
                                          </Box>
                                        </Box>
                                      }
                                    />
                                    <FormControlLabel
                                      value={2}
                                      control={<Radio color="warning" />}
                                      label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Warning sx={{ color: '#f59e0b' }} />
                                          <Box>
                                            <Typography variant="body1" fontWeight="bold" color="#f59e0b">
                                              Late Registration
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Extended registration period - new registrations marked as late
                                            </Typography>
                                          </Box>
                                        </Box>
                                      }
                                    />
                                    <FormControlLabel
                                      value={3}
                                      control={<Radio color="error" />}
                                      label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Warning sx={{ color: '#dc2626' }} />
                                          <Box>
                                            <Typography variant="body1" fontWeight="bold" color="#dc2626">
                                              Post Late Registration
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Final registration period - severely restricted registration
                                            </Typography>
                                          </Box>
                                        </Box>
                                      }
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </Paper>
                          </Box>
                        </Grid>

                        {/* Right Column - Current Settings Display */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                            Current Configuration
                          </Typography>
                          <Card sx={{ bgcolor: '#f8f9fa' }}>
                            <CardContent>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Active Session
                                </Typography>
                                <Typography variant="body1" fontWeight="600">
                                  {sessionYear || "Not set"}
                                </Typography>
                              </Box>
                              <Divider sx={{ my: 2 }} />
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  System Mode / Registration Status
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Chip 
                                    label={systemStatus === 1 ? "System Open" : "System Closed"}
                                    size="small"
                                    color={systemStatus === 1 ? "success" : "error"}
                                    variant="outlined"
                                  />
                                  <Chip 
                                    label={
                                      registrationStatus === 1 ? "Normal" :
                                      registrationStatus === 2 ? "Late" : "Post Late"
                                    }
                                    size="small"
                                    color={
                                      registrationStatus === 1 ? "success" :
                                      registrationStatus === 2 ? "warning" : "error"
                                    }
                                  />
                                </Box>
                              </Box>
                              <Divider sx={{ my: 2 }} />
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Last Updated
                                </Typography>
                                <Typography variant="body2">
                                  {currentSettings?.updatedAt 
                                    ? new Date(currentSettings.updatedAt).toLocaleString()
                                    : "Not available"}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      {/* Save Button */}
                      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e5e7eb' }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          3. Save Changes
                        </Typography>
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          startIcon={<Save />}
                          onClick={() => showConfirmDialog(
                            "Save Settings",
                            `Are you sure you want to save these settings?\n\nSession Year: ${sessionYear}\nSystem Status: ${systemStatus === 1 ? 'Open' : 'Closed'}\nRegistration Status: ${
                              registrationStatus === 1 ? 'Normal' :
                              registrationStatus === 2 ? 'Late' : 'Post Late'
                            }`,
                            handleSaveSettings
                          )}
                          disabled={saving}
                          sx={{ bgcolor: "#243b5a", py: 1.5 }}
                        >
                          {saving ? <CircularProgress size={24} sx={{ mr: 1 }} /> : "Save Changes"}
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Information Box */}
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<Info />}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: "#1565c0" }}>
                        About Registration Control
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#0d47a1" }}>
                        • <strong>Normal Registration:</strong> Standard registration period - all new student registrations proceed normally.<br />
                        • <strong>Late Registration:</strong> Extended registration period - new students registering are automatically marked as late registrations.<br />
                        • <strong>Post Late Registration:</strong> Final registration period - severely restricted registration with additional penalties.<br />
                        • Changes take effect immediately for all new registrations.<br />
                        • Existing student records are not affected by status changes.
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              )}
            </Container>

            {/* FOOTER */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={snackbar.severity}
              sx={{ width: '100%', boxShadow: 3 }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmDialog.open}
            onClose={handleCloseConfirmDialog}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
          >
            <DialogTitle id="confirm-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" />
              {confirmDialog.title}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="confirm-dialog-description" sx={{ whiteSpace: 'pre-line' }}>
                {confirmDialog.message}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseConfirmDialog} color="primary">
                Cancel
              </Button>
              <Button onClick={handleConfirmAction} color="primary" variant="contained" autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
      </IonContent>
    </IonPage>
  </ThemeProvider>
);
};

export default AdminSettings;